/**
 * Editing a song's values, the one way every screen does it.
 *
 * The tag editor page, the details panel, a right-click on a cell and "Review
 * values" all come through here, so they agree on which columns can be edited,
 * how a value is written (into the file for a tag, into Nova for its own
 * columns), and how it is undone. Every write carries a batch id: one Save is
 * one batch, so the history can put it back as one.
 */

import { engineBase, type CustomColumn } from "../api";
import { toast } from "../shell/Toast";
import type { Track } from "../types";
import { bus } from "./bus";

export type ValueType = "text" | "number" | "boolean" | "date";

export interface EditableColumn {
  /** The grid column id, also what /api/values is asked about. */
  id: string;
  label: string;
  /** Where it lives: a tag in the file, or one of Nova's own columns. */
  kind: "tag" | "column";
  /** The logical tag field, a NOVA_ custom tag name (without prefix), or the column key. */
  field: string;
  /** A Nova-namespaced custom tag (sub-genre) rather than a standard field. */
  novaTag?: boolean;
  type: ValueType;
  long?: boolean;
}

/** The tags a person edits, in the order the editor shows them. */
export const TAG_COLUMNS: EditableColumn[] = [
  { id: "title", label: "Title", kind: "tag", field: "title", type: "text" },
  { id: "artist", label: "Artist", kind: "tag", field: "artist", type: "text" },
  { id: "album", label: "Album", kind: "tag", field: "album", type: "text" },
  { id: "album_artist", label: "Album artist", kind: "tag", field: "album_artist", type: "text" },
  { id: "year", label: "Year", kind: "tag", field: "year", type: "text" },
  { id: "track_number", label: "Track number", kind: "tag", field: "track_number", type: "text" },
  { id: "genre", label: "Genre", kind: "tag", field: "genre", type: "text" },
  { id: "subgenre", label: "Sub-genre", kind: "tag", field: "SUBGENRE", novaTag: true, type: "text" },
  { id: "composer", label: "Composer", kind: "tag", field: "composer", type: "text" },
  { id: "label", label: "Label", kind: "tag", field: "label", type: "text" },
  { id: "language", label: "Language", kind: "tag", field: "language", type: "text" },
  { id: "isrc", label: "ISRC", kind: "tag", field: "isrc", type: "text" },
  { id: "comment", label: "Comment", kind: "tag", field: "comment", type: "text", long: true },
];

/** Grid columns that show a tag under another id. */
const GRID_ALIASES: Record<string, string> = { subGenre: "subgenre" };

export function editableColumns(custom: CustomColumn[]): EditableColumn[] {
  return [
    ...TAG_COLUMNS,
    ...custom.map((c) => ({ id: c.key, label: c.label, kind: "column" as const, field: c.key, type: c.type })),
  ];
}

/** The editable column behind a grid column id, if it can be edited. */
export function editableFor(columnId: string | null | undefined, custom: CustomColumn[]): EditableColumn | null {
  if (!columnId) return null;
  const id = GRID_ALIASES[columnId] ?? columnId;
  return editableColumns(custom).find((c) => c.id === id) ?? null;
}

/** What the list already knows for a song (the editor asks the file for the rest). */
export function knownValue(track: Track, col: EditableColumn): unknown {
  if (col.kind === "column") return track.custom?.[col.field] ?? null;
  switch (col.id) {
    case "title": return track.title || null;
    case "artist": return track.artist || null;
    case "album": return track.album ?? null;
    case "year": return track.year ?? null;
    case "genre": return track.genre ?? null;
    case "subgenre": return track.subGenre ?? null;
    case "comment": return track.comment ?? null;
    default: return undefined;
  }
}

/** Text the person typed, as the column stores it. "" clears the value. */
export function parseValue(col: EditableColumn, text: string): { value: string | number | boolean | null; error?: string } {
  const t = text.trim();
  if (t === "") return { value: null };
  if (col.kind === "tag") return { value: text };
  if (col.type === "number") {
    const n = Number(t.replace(",", "."));
    return Number.isFinite(n) ? { value: n } : { value: null, error: `${col.label} takes a number` };
  }
  if (col.type === "boolean") {
    const lower = t.toLowerCase();
    if (["yes", "true", "1", "y"].includes(lower)) return { value: true };
    if (["no", "false", "0", "n"].includes(lower)) return { value: false };
    return { value: null, error: `${col.label} takes yes or no` };
  }
  if (col.type === "date") {
    return /^\d{4}-\d{2}-\d{2}$/.test(t) ? { value: t } : { value: null, error: `${col.label} takes a date, like 2024-05-31` };
  }
  return { value: text };
}

export function showValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (value === true) return "yes";
  if (value === false) return "no";
  return String(value);
}

export function newBatch(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
}

async function post<T>(path: string, body: unknown): Promise<T & { error?: string }> {
  try {
    const res = await fetch(`${engineBase()}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return (await res.json()) as T & { error?: string };
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) } as T & { error?: string };
  }
}

export interface EditOutcome {
  error?: string;
  batch: string;
  /** Songs where the write failed, with the reason. */
  failed: { trackId: string; error: string }[];
}

/**
 * Write several columns to several songs as one batch.
 * `values` maps an editable column id to its new value (null clears it).
 */
export async function writeValues(
  trackIds: string[],
  values: { col: EditableColumn; value: string | number | boolean | null }[],
  batch = newBatch()
): Promise<EditOutcome> {
  const fields: Record<string, string | null> = {};
  const custom: Record<string, string | null> = {};
  const columnValues: Record<string, string | number | boolean | null> = {};
  for (const { col, value } of values) {
    if (col.kind === "column") columnValues[col.field] = value;
    else if (col.novaTag) custom[col.field] = value === null ? null : String(value);
    else fields[col.field] = value === null ? null : String(value);
  }
  const failed: EditOutcome["failed"] = [];
  const one = trackIds.length === 1;
  const errors: string[] = [];

  if (Object.keys(fields).length || Object.keys(custom).length) {
    const res = one
      ? await post<{ ok?: boolean }>("/api/tags", { trackId: trackIds[0], fields, custom, batch })
      : await post<{ results?: { trackId: number; status: string; error?: string }[] }>(
          "/api/tracks/tag", { trackIds, fields, custom, batch }
        );
    if (res.error) errors.push(res.error);
    for (const r of ("results" in res ? res.results : undefined) ?? []) {
      if (r.status === "failed") failed.push({ trackId: String(r.trackId), error: r.error ?? "not written" });
    }
  }
  if (Object.keys(columnValues).length) {
    if (one) {
      for (const [key, value] of Object.entries(columnValues)) {
        const res = await post("/api/columns/value", { trackId: trackIds[0], key, value, batch });
        if (res.error) errors.push(res.error);
      }
    } else {
      const res = await post<{ results?: { trackId: number; status: string; error?: string }[] }>(
        "/api/columns/values", { trackIds, values: columnValues, batch }
      );
      if (res.error) errors.push(res.error);
      for (const r of res.results ?? []) {
        if (r.status === "failed") failed.push({ trackId: String(r.trackId), error: r.error ?? "not stored" });
      }
    }
  }
  bus.emit("library.edited", { trackIds, batch });
  return { error: errors.length ? errors.join("; ") : undefined, batch, failed };
}

/** Put a batch back. */
export async function undoBatch(batch: string): Promise<string | null> {
  const res = await post<{ reverted?: number }>("/api/history/revert", { batch });
  bus.emit("library.edited", { batch });
  return res.error ?? null;
}

/** Put chosen history entries back. */
export async function revertEntries(ids: number[]): Promise<string | null> {
  const res = await post<{ reverted?: number }>("/api/history/revert", { ids });
  bus.emit("library.edited", { ids });
  return res.error ?? null;
}

/** After a write: say what happened, with Undo. */
export function announce(outcome: EditOutcome, what: string, onDone?: () => void) {
  if (outcome.error && !outcome.failed.length) {
    toast(`Not saved: ${outcome.error}`, "bad");
    return;
  }
  const failed = outcome.failed.length ? ` ${outcome.failed.length} could not be written: ${outcome.failed[0].error}` : "";
  toast(`${what}${failed}`, failed ? "bad" : "ok", 4000, {
    label: "Undo",
    run: async () => {
      const error = await undoBatch(outcome.batch);
      toast(error ? `Could not undo: ${error}` : "Undone.", error ? "bad" : "ok");
      onDone?.();
    },
  });
  onDone?.();
}

export interface ValueCount {
  value: unknown;
  count: number;
  trackIds: string[];
}

/** Every distinct value of a column, with its songs. Without ids, the whole library. */
export async function fetchValues(columnId: string, trackIds?: string[]): Promise<{ values: ValueCount[]; songs: number; error?: string }> {
  const res = await post<{ values?: ValueCount[]; songs?: number }>("/api/values", { field: columnId, trackIds });
  return { values: res.values ?? [], songs: res.songs ?? 0, error: res.error };
}

export interface HistoryEntry {
  id: number;
  batch: string;
  at: string;
  trackId: string | null;
  kind: "tag" | "column" | "cover";
  field: string;
  old: unknown;
  new: unknown;
  hadCover: boolean;
  source: string;
  revertedBy: string | null;
}

export async function fetchHistory(trackId: string): Promise<HistoryEntry[]> {
  try {
    const res = await fetch(`${engineBase()}/api/history?trackId=${encodeURIComponent(trackId)}`);
    return ((await res.json()) as { entries?: HistoryEntry[] }).entries ?? [];
  } catch {
    return [];
  }
}

/** A history field name as people say it. */
export function fieldLabel(entry: Pick<HistoryEntry, "kind" | "field">, custom: CustomColumn[]): string {
  if (entry.kind === "cover") return "Cover";
  if (entry.kind === "column") return custom.find((c) => c.key === entry.field)?.label ?? entry.field;
  const name = entry.field.replace(/^NOVA_/, "");
  return TAG_COLUMNS.find((c) => c.field === entry.field || c.field === name)?.label ?? name.replace(/_/g, " ").toLowerCase();
}
